import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { ApexOptions } from 'apexcharts';
import { CardBody, CardHeader } from 'grommet';
import { Box, Card, CardProps, LoadingSpinner, Text } from '/src/components';
import { DashboardData } from '/src/lib/models';
import { Nullable } from '/src/lib/types';
import theme from '/src/theme';

const themeColors = theme.global?.colors ?? {};

const cardHeight = '40.875rem';
const colors: string[] = ['#eba14d', themeColors['accent-3'] as string, themeColors['accent-5'] as string, '#4de6eb'];

const SCALE_FACTORS: Record<string, number> = {
  'Electric Forklift': 1.5,
  eTRU: 8,
  'Fixed Guideway, Light Rail': 6,
  'Heavy-Duty On-Road EV': 3.5,
  eCHE: 15,
  'Light-Duty On-Road EV': 2.5,
};

const DEFAULT_SCALE_FACTOR_RANGE = { min: 5, max: 10, multiplier: 0.8 };

const getChartOptions = (reportingPeriodLabel: string): ApexOptions => ({
  chart: {
    type: 'line',
    fontFamily: 'Roboto Condensed, sans-serif',
    foreColor: themeColors['dark-3'] as string,
    animations: {
      enabled: true,
      speed: 500,
      animateGradually: {
        enabled: false,
      },
      dynamicAnimation: {
        speed: 300,
      },
    },
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  stroke: {
    width: [2.5],
    curve: 'smooth',
  },
  dataLabels: {
    enabled: true,
    enabledOnSeries: [0],
    style: {
      fontSize: '11px',
      fontWeight: 600,
    },
    background: {
      borderColor: themeColors.brand as string,
      borderRadius: 10,
      padding: 5,
    },
  },
  markers: {
    size: 0,
    hover: { size: 6 },
  },
  colors,
  fill: {
    type: 'solid',
  },
  grid: {
    borderColor: '#EAECEF',
    strokeDashArray: 4,
    xaxis: {
      lines: { show: false },
    },
    padding: { left: 10, right: 10 },
  },
  xaxis: {
    type: 'category',
    axisBorder: { show: false },
    axisTicks: { show: false },
    title: {
      text: reportingPeriodLabel,
      style: {
        fontWeight: 400,
        fontSize: '20px',
        color: themeColors['dark-3'] as string,
      },
      offsetY: -10,
    },
    labels: {
      rotate: -90,
      rotateAlways: true,
      offsetX: -1,
      style: {
        fontWeight: 500,
      },
    },
  },
  yaxis: {
    show: false,
  },
  tooltip: {
    theme: 'light',
    y: {
      formatter: (val) => val?.toString().split('.')[0] ?? '',
    },
    style: {
      fontSize: '13px',
    },
  },
  legend: {
    showForSingleSeries: true,
    fontSize: '16px',
    markers: {
      radius: 12,
    },
  },
});

export const EnrollmentTrendsChart: React.FC<EnrollmentTrendsChartProps> = (props) => {
  /**
   * Props
   */
  const { data, height, ...cardProps } = props;
  const { t } = useTranslation();

  const { chartData, chartLabels, categoryStrokes, chartYAxes } = useMemo(() => {
    const periods: string[] = [];
    const categories: Record<string, Nullable<number>[]> = { Facilities: [] };
    const categoryMaxValues: Record<string, number> = {};

    const equipmentCategories: string[] = [];
    data.reporting_periods.forEach((period) => {
      period.equipment.forEach((equipment) => {
        if (!equipmentCategories.includes(equipment.category)) {
          equipmentCategories.push(equipment.category);
          categories[equipment.category] = [];
        }
      });
    });

    data.reporting_periods.forEach((period) => {
      periods.push(period.reporting_period);

      categories['Facilities'].push(period.num_facilities);
      categoryMaxValues['Facilities'] = Math.max(categoryMaxValues['Facilities'] ?? 0, period.num_facilities);

      equipmentCategories.forEach((categoryName) => {
        const equipment = period.equipment.find((equipment) => equipment.category === categoryName);

        categories[categoryName].push(equipment?.amount ?? null);
        if (equipment) {
          categoryMaxValues[categoryName] = Math.max(categoryMaxValues[categoryName] ?? 0, equipment.amount);
        }
      });
    });

    const series: ApexAxisChartSeries = [];
    const categoryYAxes: ApexYAxis[] = [];
    const strokes: number[] = [];

    series.push({
      name: 'Facilities',
      type: 'line',
      data: categories['Facilities'],
    });

    const highestEquipmentValue = equipmentCategories.length
      ? Math.max(...equipmentCategories.map((cat) => categoryMaxValues[cat]))
      : 0;

    const facilitiesMax = categoryMaxValues['Facilities'];
    const facilitiesYAxis: ApexYAxis = {
      seriesName: 'Facilities',
      show: false,
      ...(facilitiesMax > 0 ? { max: facilitiesMax * 1.15 } : {}),
    };

    equipmentCategories.forEach((categoryName) => {
      strokes.push(0);

      series.push({
        name: categoryName,
        type: 'column',
        data: categories[categoryName],
      });

      const categoryMax = categoryMaxValues[categoryName];

      const scaleFactor =
        SCALE_FACTORS[categoryName] ??
        (categoryMax > 0
          ? Math.min(
              DEFAULT_SCALE_FACTOR_RANGE.max,
              Math.max(
                DEFAULT_SCALE_FACTOR_RANGE.min,
                (highestEquipmentValue / categoryMax) * DEFAULT_SCALE_FACTOR_RANGE.multiplier
              )
            )
          : DEFAULT_SCALE_FACTOR_RANGE.min);

      categoryYAxes.push({
        seriesName: categoryName,
        show: false,
        ...(categoryMax > 0 ? { max: categoryMax * scaleFactor } : {}),
      });
    });

    return {
      chartData: series,
      chartLabels: periods,
      chartYAxes: [facilitiesYAxis, ...categoryYAxes],
      categoryStrokes: strokes,
    };
  }, [data]);

  return (
    <Card flex="grow" height={height || cardHeight} {...cardProps}>
      <CardHeader>
        <Text size="xlarge">{t('dashboard:cumulativeEnrollmentTrends')}</Text>
      </CardHeader>
      <CardBody pad={{ horizontal: 'medium', bottom: '2rem' }}>
        {!chartLabels || !categoryStrokes || !chartYAxes || !chartData ? (
          <LoadingSpinner />
        ) : (
          <Box width="100%" flex="grow">
            <Chart
              options={{
                ...getChartOptions(t('common:reportingPeriod')),
                labels: chartLabels,
                yaxis: chartYAxes,
                stroke: {
                  width: [4, ...categoryStrokes],
                },
                plotOptions: {
                  bar: {
                    columnWidth: '65%',
                    borderRadius: 2,
                    borderRadiusApplication: 'end',
                    dataLabels: { maxItems: 1000 },
                  },
                },
              }}
              series={chartData}
              type="line"
              width="100%"
              height="100%"
            />
          </Box>
        )}
      </CardBody>
    </Card>
  );
};

export type EnrollmentTrendsChartProps = CardProps & {
  data: DashboardData;
};
